import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import {
  getAllActions, retryAction, recoverInterrupted, getUnownedCount, setQueueScope, queueScope,
  syncAll,
  type QueuedActionType,
  enqueue,
} from '@/lib/offline-queue';
import { useAuth } from './AuthContext';

type OfflineContextValue = {
  isOnline: boolean;
  pendingCount: number;
  failedCount: number;
  unownedCount: number;
  storageError: string | null;
  isSyncing: boolean;
  syncProgress: { current: number; total: number } | null;
  /** Queue an action for offline-safe submission */
  queueAction: (
    type: QueuedActionType,
    windrowId: number,
    payload: Record<string, unknown>,
    photoBlob?: Blob
  ) => Promise<void>;
  /** Try to sync right now */
  triggerSync: () => Promise<void>;
  /** Refresh the pending count */
  refreshCount: () => Promise<void>;
};

const OfflineContext = createContext<OfflineContextValue | null>(null);

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const { token, user } = useAuth();
  const scope = queueScope(user);
  const [failedCount, setFailedCount] = useState(0);
  const [unownedCount, setUnownedCount] = useState(0);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number } | null>(null);

  // Watch network status
  useEffect(() => {
    // Set initial status
    if (Capacitor.isNativePlatform()) {
      Network.getStatus().then((status) => setIsOnline(status.connected));
    } else {
      setIsOnline(navigator.onLine);
    }

    // Listen for changes
    if (Capacitor.isNativePlatform()) {
      const handler = Network.addListener('networkStatusChange', (status) => {
        setIsOnline(status.connected);
      });
      return () => {
        handler.then((h) => h.remove());
      };
    } else {
      const onOnline = () => setIsOnline(true);
      const onOffline = () => setIsOnline(false);
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
      };
    }
  }, []);

  const refreshCount = useCallback(async () => {
    if (!scope) { setPendingCount(0); setFailedCount(0); setUnownedCount(0); return; }
    const actions = await getAllActions();
    setPendingCount(actions.filter(action => action.status !== 'synced').length);
    setFailedCount(actions.filter(action => action.status === 'failed').length);
    setUnownedCount(await getUnownedCount());
    setStorageError(null);
  }, [scope]);

  useEffect(() => {
    setQueueScope(scope);
    void recoverInterrupted().then(refreshCount).catch(() => setStorageError('Cannot read saved entries on this device. Do not clear browser data; retry or contact support.'));
    return () => setQueueScope(null);
  }, [scope, refreshCount]);

  // Refresh count on mount and when online status changes
  useEffect(() => {
    void refreshCount().catch(() => setStorageError('Cannot read saved entries on this device.'));
  }, [isOnline, refreshCount]);

  const queueAction = useCallback(
    async (
      type: QueuedActionType,
      windrowId: number,
      payload: Record<string, unknown>,
      photoBlob?: Blob
    ) => {
      await enqueue(type, windrowId, payload, photoBlob);
      // Persistence is the save boundary; a badge refresh failure must not invite a duplicate save.
      await refreshCount().catch(() => setStorageError('Your entry was saved, but upload status could not be refreshed.'));

    },
    [refreshCount]
  );

  const triggerSync = useCallback(async () => {
    if (!token || !scope || isSyncing) return;
    setIsSyncing(true);
    setSyncProgress(null);

    try {
      for (const action of await getAllActions()) {
        if (action.status === 'failed') await retryAction(action.id);
      }
      await syncAll(token, (current, total) => {
        setSyncProgress({ current, total });
      });
    } catch {
      setStorageError('Unable to access saved entries. They have not been confirmed as uploaded.');
    } finally {
      setIsSyncing(false);
      setSyncProgress(null);
      await refreshCount();
    }
  }, [token, scope, isSyncing, refreshCount]);

  // Auto-submit only fresh pending work; failures require an explicit retry.
  useEffect(() => {
    if (isOnline && pendingCount > 0 && failedCount === 0 && token && scope && !isSyncing) {
      setIsSyncing(true);
      void syncAll(token, (current, total) => setSyncProgress({ current, total })).catch(() => setStorageError('Unable to access saved entries. They have not been confirmed as uploaded.')).finally(() => { setIsSyncing(false); setSyncProgress(null); void refreshCount(); });
    }
  }, [isOnline, pendingCount, failedCount, token, scope, refreshCount]);

  const value: OfflineContextValue = {
    isOnline,
    pendingCount,
    failedCount,
    unownedCount,
    storageError,
    isSyncing,
    syncProgress,
    queueAction,
    triggerSync,
    refreshCount,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
}
